// Pure billing helpers: price formatting, savings math and the UI decisions
// (plan CTAs, status badge, banner). Kept free of React so they are unit tested.

import { fmt } from "./time";
import type { Billing, BillingInterval, Plan, PlanId, Price } from "./types";

const DAY_MS = 86_400_000;

export const PLAN_RANK: Record<PlanId, number> = { free: 0, standard: 1, team: 2, pro: 3 };

/** Statuses of a Stripe subscription that still grant its plan. */
const LIVE_STATUSES = new Set(["active", "trialing", "past_due"]);

// ---- money -------------------------------------------------------------------

function fractionDigits(currency: string) {
  try {
    return (
      new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/**
 * Formats an amount in minor units ("3900", "usd") as "$39". Whole amounts drop
 * the decimals; others keep the currency's precision ("$32.50").
 */
export function formatPrice(price: Price, locale?: string) {
  const currency = price.currency.toUpperCase();
  const digits = fractionDigits(currency);
  const factor = 10 ** digits;
  const whole = price.amount % factor === 0;

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: whole ? 0 : digits,
      maximumFractionDigits: digits,
    }).format(price.amount / factor);
  } catch {
    return `${(price.amount / factor).toFixed(whole ? 0 : digits)} ${currency}`;
  }
}

export interface YearlySavings {
  /** minor units saved per year compared with paying monthly */
  amount: number;
  currency: string;
  /** whole percent, rounded down so the claim is never overstated */
  percent: number;
  /** set only when the saving is exactly a whole number of monthly payments */
  monthsFree: number | null;
}

/** What a yearly price saves over twelve monthly payments, or null when it saves nothing. */
export function yearlySavings(month: Price | null, year: Price | null): YearlySavings | null {
  if (!month || !year || month.amount <= 0) return null;
  if (month.currency.toLowerCase() !== year.currency.toLowerCase()) return null;

  const fullYear = month.amount * 12;
  const saved = fullYear - year.amount;
  if (saved <= 0) return null;

  const months = saved / month.amount;

  return {
    amount: saved,
    currency: month.currency,
    percent: Math.floor((saved * 100) / fullYear),
    monthsFree: Number.isInteger(months) ? months : null,
  };
}

export function savingsLabel(s: YearlySavings) {
  if (s.monthsFree) return `${s.monthsFree} month${s.monthsFree === 1 ? "" : "s"} free`;

  return `Save ${s.percent}%`;
}

/**
 * One label for the interval toggle: "2 months free" when every priced plan
 * saves exactly that, otherwise "Save up to N%". Null when yearly saves nothing.
 */
export function savingsSummary(plans: Plan[]): string | null {
  const all = plans.map((p) => yearlySavings(p.prices.month, p.prices.year)).filter((s) => s !== null);
  if (all.length === 0) return null;

  const first = all[0]!;
  if (first.monthsFree && all.every((s) => s.monthsFree === first.monthsFree)) return savingsLabel(first);

  return `Save up to ${Math.max(...all.map((s) => s.percent))}%`;
}

// ---- subscription state ------------------------------------------------------------

export function hasLiveSubscription(b: Pick<Billing, "source" | "status">) {
  return b.source === "stripe" && LIVE_STATUSES.has(b.status);
}

/** Whole days left until `iso` (rounded up, never negative). */
export function daysLeft(iso: string | null | undefined, now: Date = new Date()) {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - now.getTime();

  return ms > 0 ? Math.ceil(ms / DAY_MS) : 0;
}

export function daysLabel(n: number) {
  return `${n} day${n === 1 ? "" : "s"} left`;
}

export type Tone = "neutral" | "success" | "warning" | "danger" | "info";

export interface StatusBadge {
  label: string;
  tone: Tone;
}

/** The badge next to the plan name in Settings → Billing. */
export function statusBadge(b: Billing, tz: string, now: Date = new Date()): StatusBadge | null {
  if (!b.enabled) return null;

  if (b.source === "trial")
    return { label: `Trial · ${daysLabel(daysLeft(b.trial_ends_at, now))}`, tone: "info" };
  if (b.source === "free") return { label: "Free", tone: "neutral" };
  if (b.source === "comp")
    return {
      label: b.comp_until ? `Complimentary until ${fmt(b.comp_until, tz, "MMM d, yyyy")}` : "Complimentary",
      tone: "success",
    };
  if (b.source !== "stripe") return null;

  if (b.status === "past_due") return { label: "Past due", tone: "danger" };
  if (b.cancel_at_period_end && b.current_period_end)
    return { label: `Cancels on ${fmt(b.current_period_end, tz, "MMM d, yyyy")}`, tone: "warning" };
  if (b.status === "trialing")
    return {
      label: b.trial_ends_at ? `Trial · ${daysLabel(daysLeft(b.trial_ends_at, now))}` : "Trial",
      tone: "info",
    };
  if (b.status === "active") return { label: "Active", tone: "success" };

  return { label: b.status.replace(/_/g, " "), tone: "neutral" };
}

// ---- plan CTA ------------------------------------------------------------------------

export type PlanAction =
  | { kind: "current"; label: "Current plan" }
  | { kind: "checkout"; label: "Upgrade" | "Choose plan" }
  | { kind: "portal"; label: "Switch plan" }
  | { kind: "unavailable"; label: "Unavailable" }
  /** no button: billing off, a read-only viewer, or Free without a subscription to cancel */
  | { kind: "none" };

/** What the button on a plan card does for this workspace. */
export function planAction(b: Billing, plan: Plan, interval: BillingInterval): PlanAction {
  if (!b.enabled) return { kind: "none" };

  const live = hasLiveSubscription(b);
  const samePlan = plan.id === b.plan;
  const current =
    samePlan &&
    (b.source === "free" ||
      b.source === "comp" ||
      (live && (b.interval === null || b.interval === interval)));

  if (current) return { kind: "current", label: "Current plan" };
  if (!b.can_manage) return { kind: "none" };
  // plan changes (and downgrades to Free) of a live subscription go through the Stripe portal
  if (live) return { kind: "portal", label: "Switch plan" };
  // Free is what a workspace falls back to; there is nothing to buy
  if (plan.id === "free") return { kind: "none" };
  if (!plan.prices[interval]) return { kind: "unavailable", label: "Unavailable" };

  return { kind: "checkout", label: b.source === "free" ? "Upgrade" : "Choose plan" };
}

// ---- banner ------------------------------------------------------------------------------

export const TRIAL_BANNER_DAYS = 7;

export type Banner = { kind: "trial"; daysLeft: number; plan: PlanId } | { kind: "past_due" };

/** The thin banner above every app page, or null when there is nothing to say. */
export function bannerFor(b: Billing | undefined, now: Date = new Date()): Banner | null {
  if (!b?.enabled) return null;
  if (b.source === "stripe" && b.status === "past_due") return { kind: "past_due" };
  if (b.source === "trial") {
    const left = daysLeft(b.trial_ends_at, now);
    if (left > 0 && left <= TRIAL_BANNER_DAYS) return { kind: "trial", daysLeft: left, plan: b.plan };
  }

  return null;
}

// ---- limits ------------------------------------------------------------------------------

export interface Meter {
  used: number;
  /** null = unlimited */
  max: number | null;
  /** used >= max */
  reached: boolean;
  /** used > max: after a downgrade */
  over: boolean;
}

export function meter(used: number, max: number | null): Meter {
  return { used, max, reached: max !== null && used >= max, over: max !== null && used > max };
}

export function planName(id: string, plans?: Plan[]) {
  return plans?.find((p) => p.id === id)?.name ?? id.charAt(0).toUpperCase() + id.slice(1);
}

export function billingPath(orgId: string) {
  return `/o/${orgId}/settings/billing`;
}

/** The org id of the current app URL (/o/:orgId/...), if any. */
export function orgIdFromPath(pathname: string) {
  return /^\/o\/([^/]+)/.exec(pathname)?.[1] ?? null;
}

/** Only follow redirect URLs that are web pages (the server returns Stripe URLs). */
export function isSafeRedirect(url: string) {
  try {
    const u = new URL(url);

    return u.protocol === "https:" || (u.protocol === "http:" && u.hostname === "localhost");
  } catch {
    return false;
  }
}
